import { useEffect, useState } from "react";
import { useWatch } from "react-hook-form";
import CommentGroup from "../../../ADP/CommentGroup";
import styles from "./index.module.scss";
import TextArea from "../../../TextArea";
import CheckboxGroup from "../../../CheckboxGroup";
import RadioGroup from "../../../RadioGroup";
import ToggleAllUnitsButton from "../../../ToggleButton/ToggleAllUnitsButton";
import PageMarker from "../../../ADP/PageMarker";
import { Text } from "../../../Type";
import Tooltip from "../../../Tooltip";
import { useStoreContext } from "../../../../contexts/StoreProvider";
import Modal from "../../../Modal";
import Button from "../../../Button";
import TextGrid from "../../../TextGrid";
import TextLink from "../../../TextLinks";
import { useToggleAllUnits } from "../../../../hooks/useToggleAllUnits";
import MeasurementInput from "../../../MeasurementInput";
import UploadDocumentsModal from "../../../ADP/UploadDocumentsModal";
import {
  customModificationsOptions,
  reasonOptions,
} from "../../../../utils/labelMap/LabelMap";
import { nonSpecialCharacterOptions } from "../../../../utils/forms/validation";
import { useClientFileContext } from "../../../../contexts/ClientFileProvider";
import { useTriggerOnFieldChange } from "../../../../hooks/useTriggerOnFieldChange";

const Section2c = () => {
  const {
    state: { user, externalUrls },
  } = useStoreContext();
  const [isUploadModalOpen, setUploadModalOpen] = useState(false);
  const { clientFile, formMode: mode } = useClientFileContext();

  const isSalesRep = user?.role.name === "Sales Rep";
  const fileStatus = clientFile.status;

  const currDevice = useWatch({ name: "section2c.base-device" });
  const [prescriptionDetailsRequired, setPrescriptionDetailsRequired] =
    useState(false);
  const reasonForApplication2c = useWatch({
    name: "section2c.reason-for-application",
  });
  useEffect(() => {
    // LOGIC for conditional requiredText for "Prescription Details"
    if (reasonForApplication2c === "modifications-previous") {
      setPrescriptionDetailsRequired(false);
    } else if (currDevice !== "none" && currDevice !== "power-scooter") {
      setPrescriptionDetailsRequired(true);
    } else {
      setPrescriptionDetailsRequired(false);
    }
  }, [
    setPrescriptionDetailsRequired,
    currDevice,
    reasonForApplication2c,
  ]);

  useTriggerOnFieldChange("section2c.reason-for-application", "section2c.replacement-devices");
  // #region options
  const baseDeviceOptions = [
    {
      label: "Adult Power Base Type 1",
      value: "apbt1",
      tooltip: (
        <Tooltip
          id="apbt1-tooltip"
          content="Most suitable for use on hard, flat surfaces. Primarily for user who requires minimal adjustments to support independent mobility. ADP price includes seat and back frame (sizes up to 18&Prime; wide x 18&Prime;deep), standard joystick control, swing away detachable footrests, height adjustable armrests, 2 point seat belt any closure, front casters, wheels. See Power Wheelchair Options Chart for other basic components funded by ADP.
        Please refer to the ADP product manual for more details."
          links={[
            {
              id: "link-1",
              text: "ADP Manual",
              href: externalUrls.mobility_devices_product_manual,
            },
          ]}
        />
      ),
    },
    {
      label: "Adult Power Base Type 2",
      value: "apbt2",
      tooltip: (
        <Tooltip
          id="apbt2-tooltip"
          content="Suitable for use on most surfaces. Primarily for user who requires postural support, modifications to electronics, and other specific options to support independent mobility. ADP price includes seat and back frame (sizes up to 18&Prime; wide x 18&Prime;deep), standard joystick control, swing away detachable footrests, height adjustable armrests, 2 point seat belt any closure, front casters, and wheels. See Power Wheelchair Options Chart for other basic components funded by ADP.
        Please refer to the ADP product manual for more details."
          links={[
            {
              id: "link-1",
              text: "ADP Manual",
              href: externalUrls.mobility_devices_product_manual,
            },
          ]}
        />
      ),
    },
    {
      label: "Adult Power Base Type 3",
      value: "apbt3",
      tooltip: (
        <Tooltip
          id="apbt3-tooltip"
          content="Suitable for use on most surfaces. Primarily for the user who requires ongoing changes in postural support, modifications to electronics, and other specific options to support independent mobility. ADP price includes base, 2 point seat belt any closure and standard joystick control. See Power Wheelchair Options Chart for other basic components funded by ADP.
        Please refer to the ADP product manual for more details."
          links={[
            {
              id: "link-1",
              text: "ADP Manual",
              href: externalUrls.mobility_devices_product_manual,
            },
          ]}
        />
      ),
    },
    {
      label: "Paediatric Power Base Type 1",
      value: "ppbt1",
      tooltip: (
        <Tooltip
          id="ppbt1-tooltip"
          content="Most suitable for use on hard, flat surfaces. Primarily for user who requires minimal adjustments to support independent mobility. ADP price includes seat and back frame (sizes up to 16&Prime; wide x 16&Prime;deep), standard joystick control, swing away detachable footrests, height adjustable armrests, 2 point seat belt any closure, front casters, wheels. See Power Wheelchair Options Chart for other basic components funded by ADP.
        Please refer to the ADP product manual for more details."
          links={[
            {
              id: "link-1",
              text: "ADP Manual",
              href: externalUrls.mobility_devices_product_manual,
            },
          ]}
        />
      ),
    },
    {
      label: "Paediatric Power Base Type 2",
      value: "ppbt2",
      tooltip: (
        <Tooltip
          id="ppbt2-tooltip"
          content="Suitable for use on most surfaces. Primarily for user who requires postural support, modifications to electronics, and other specific options to support independent mobility. ADP price includes seat and back frame (sizes up to 16&Prime; wide x 16&Prime;deep), standard joystick control, swing away detachable footrests, height adjustable armrests, 2 point seat belt any closure, front casters, wheels. See Power Wheelchair Options Chart for other basic components funded by ADP.
        Please refer to the ADP product manual for more details."
          links={[
            {
              id: "link-1",
              text: "ADP Manual",
              href: externalUrls.mobility_devices_product_manual,
            },
          ]}
        />
      ),
    },
    {
      label: "Paediatric Power Base Type 3",
      value: "ppbt3",
      tooltip: (
        <Tooltip
          id="ppbt3-tooltip"
          content="Suitable for use on most surfaces. Primarily for the user who requires ongoing changes in postural support, modifications to electronics, and other specific options to support independent mobility. ADP price includes base, 2 point seat belt any closure, and standard joystick control. See Power Wheelchair Options Chart for other basic components funded by ADP.
        Please refer to the ADP product manual for more details."
          links={[
            {
              id: "link-1",
              text: "ADP Manual",
              href: externalUrls.mobility_devices_product_manual,
            },
          ]}
        />
      ),
    },
    {
      label: "Paediatric Power Base with Manual Dynamic Tilt",
      value: "ppbwmdt",
      tooltip: (
        <Tooltip
          id="ppbwmdt-tooltip"
          content="Offers manual dynamic tilt required by the user who can not maintain a functional posture through the use of seating components alone. ADP price includes dynamic tilt frame (sizes up to 16&Prime; wide x 16&Prime;deep), standard joystick control, swing away detachable footrests, height adjustable armrests, 2 point seat belt any closure, front casters, wheels. See Power Wheelchair Options Chart for other basic components funded by ADP.
        Please refer to the ADP product manual for more details."
          links={[
            {
              id: "link-1",
              text: "ADP Manual",
              href: externalUrls.mobility_devices_product_manual,
            },
          ]}
        />
      ),
    },
    {
      label: "Power Scooter",
      value: "power-scooter",
      tooltip: (
        <Tooltip
          id="power-scooter-tooltip"
          content="Suitable for the user who requires power mobility to meet their long term basic and essential mobility requirements, and does not require specific postural support now or in the future. ADP price includes base, tiller and seat. ADP funding for positioning devices (seating) is not available for scooters. See Power Wheelchair Options Chart when heavy duty models are required.
        Please refer to the ADP product manual for more details."
          links={[
            {
              id: "link-1",
              text: "ADP Manual",
              href: externalUrls.mobility_devices_product_manual,
            },
          ]}
        />
      ),
    },
    {
      label: "None",
      value: "none",
    },
  ];

  const baseMoveThroughoutOptions = [
    {
      label: "Yes",
      value: "bmt-yes",
    },
    { label: "No", value: "bmt-no" },
    { label: "N/A", value: "bmt-na" },
  ];

  const baseMoveBeyondOptions = [
    {
      label: "Yes",
      value: "bmb-yes",
    },
    { label: "No", value: "bmb-no" },
    { label: "N/A", value: "bmb-na" },
  ];

  const scooterMoveThroughoutOptions = [
    {
      label: "Yes",
      value: "smt-yes",
    },
    { label: "No", value: "smt-no" },
    { label: "N/A", value: "smt-na" },
  ];

  const scooterMoveBeyondOptions = [
    {
      label: "Yes",
      value: "smb-yes",
    },
    { label: "No", value: "smb-no" },
    { label: "N/A", value: "smb-na" },
  ];

  const scooterSeatTillerOptions = [
    {
      label: "Yes",
      value: "sst-yes",
    },
    { label: "No", value: "sst-no" },
    { label: "N/A", value: "sst-na" },
  ];

  const additionalAdpFundedOptions = [
    {
      label: "Adjustable Tension Back Upholstery",
      value: "atbu",
    },
    {
      label: "Midline Control",
      value: "midline-control",
    },
    {
      label: "Manual Recline Option",
      value: "manual-recline-control",
    },
    {
      label: "Angle Adjustable Footplates (pair)",
      value: "aafp",
    },
    {
      label: "Manual Elevating Legrests (pair)",
      value: "melp",
    },
    {
      label: "Swingaway Mounting Bracket",
      value: "smb",
    },
    {
      label: "One Piece 90/90 Front Riggings",
      value: "op9090fr",
    },
    {
      label:
        "Seat Package 1 for Power Bases (includes frame, sling upholstery, armrests, footrests)",
      value: "seat-package-1",
    },
    {
      label:
        "Seat Package 2 for Power Bases (includes deluxe seat and back, armrests, footrests)",
      value: "seat-package-2",
    },
    {
      label: "Oxygen Tank Holder",
      value: "oxygen-tank-holder",
    },
    {
      label: "Ventilator Tray",
      value: "ventilator-tray",
    },
  ];

  const specialityOptions = [
    {
      label: "Specialty Controls 1 Non Standard Joystick *",
      value: "specialty-1",
    },
    {
      label: "Specialty Controls 2 Chin/Rim Control *",
      value: "specialty-2",
    },
    {
      label: "Specialty Controls 3 Simple Touch *",
      value: "specialty-3",
    },
    {
      label: "Specialty Controls 4 Proximity Control *",
      value: "specialty-4",
    },
    {
      label: "Specialty Controls 5 Breath Control *",
      value: "specialty-5",
    },
    {
      label: "Specialty Controls 6 Scanners *",
      value: "specialty-6",
    },
    {
      label: "Auto Correction System *",
      value: "specialty-auto-correction-system",
    },
  ];

  const powerPositioningDevicesOptions = [
    {
      label: "Power Tilt Only",
      value: "ppd-1",
    },
    {
      label: "Power Recline Only",
      value: "ppd-2",
    },
    {
      label: "Power Tilt and Recline",
      value: "ppd-3",
    },
    {
      label: "Power Elevating Footrests",
      value: "ppd-4",
      tooltip: (
        <Tooltip
          id="additional-adp-funded-tooltip"
          content="Power elevating legrests can only be prescribed when the client has, or is eligible for and being prescribed power tilt and/or power recline."
        />
      ),
    },
    {
      label: "Multi-Function Control Box",
      value: "ppd-5",
    },
  ];
  // #endregion

  const replacementOptions = [
    {
      label:
        "Change in applicant’s mobility status - previously ADP funded equipment no longer meeting basic mobility needs as defined by ADP for funding purposes",
      value: "2c-change-in-status",
    },
    {
      label:
        "Change in applicant’s body size - previously ADP funded equipment is either too large or too small",
      value: "2c-change-in-size",
    },
    {
      label: "Previously ADP funded equipment is worn out",
      value: "2c-worn-out",
      link: (
        <TextLink onClick={() => setUploadModalOpen(true)}>
          - attach vendor quote and/or copies of repair bills for wheeled
          walkers and wheelchairs only.
        </TextLink>
      ),
    },
    {
      label: "Special circumstances - none of the above",
      value: "2c-special-circumstances",
      link: (
        <TextLink onClick={() => setUploadModalOpen(true)}>
          - attach letter of rationale.
        </TextLink>
      ),
    },
  ];

  // LOGIC for Units Toggle
  const { selectedUnit, toggleAllUnits } = useToggleAllUnits([
    {
      name: "section2c.seat-width-units",
      valuePrefix: "2c-seatWidth-",
      type: "length",
    },
    {
      name: "section2c.finished-back-height-units",
      valuePrefix: "2c-finishedBackHeight-",
      type: "length",
    },
    {
      name: "section2c.finished-seat-to-floor-units",
      valuePrefix: "2c-finishedSeatToFloor-",
      type: "length",
    },
    {
      name: "section2c.leg-rest-length-units",
      valuePrefix: "2c-legRestLength-",
      type: "length",
    },
    {
      name: "section2c.seat-depth-units",
      valuePrefix: "2c-seatDepth-",
      type: "length",
    },
    {
      name: "section2c.client-weight-units",
      valuePrefix: "2c-clientWeight-",
      type: "weight",
    },
  ]);

  // LOGIC for popup for additional documents: https://app.asana.com/0/0/1200354578179107/f
  const [prevReplacementDevices, setPrevReplacementDevice] = useState(
    useWatch({ name: "section2c.replacement-devices" })
  );
  const currReplacementDevice = useWatch({
    name: "section2c.replacement-devices",
  });

  const [openPreviouslyFundedModal, setOpenPreviouslyFundedModal] =
    useState(false);
  useEffect(() => {
    if (
      Array.isArray(prevReplacementDevices) &&
      Array.isArray(currReplacementDevice) &&
      (currReplacementDevice?.includes("2c-worn-out") ||
        currReplacementDevice?.includes("2c-special-circumstances")) &&
      !(
        prevReplacementDevices?.includes("2c-worn-out") ||
        prevReplacementDevices?.includes("2c-special-circumstances")
      )
    ) {
      setOpenPreviouslyFundedModal(true);
    }
    setPrevReplacementDevice(currReplacementDevice);
  }, [currReplacementDevice, prevReplacementDevices]);

  // LOGIC for clinical rationale textbox show/hide
  const customModificationsObject = useWatch({
    name: "section2c.custom-modifications-required",
  });
  const checkCustomModifications = customModificationsObject === "cm-yes";

  // LOGIC for conditional requiredText in replacement-devices
  const currIsReplacementDevicesRequired =
    reasonForApplication2c !== "first-access" &&
    reasonForApplication2c !== "another-type";
  // Need to use useState + useEffect to trigger re-renders
  const [isReplacementDevicesRequired, setIsReplacementDevicesRequired] =
    useState(currIsReplacementDevicesRequired);
  useEffect(() => {
    setIsReplacementDevicesRequired(currIsReplacementDevicesRequired);
  }, [currIsReplacementDevicesRequired]);
  return (
    <>
      <PageMarker page={7} total={13} />
      <CommentGroup
        section="section2c"
        id="1-base-device"
        className={styles.marginAfterPM}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <RadioGroup
          section="section2c"
          name="base-device"
          label="Base Device (check one)"
          options={baseDeviceOptions}
          requiredText="This field is required."
          readOnly={mode !== "edit"}
          tooltip={
            <Tooltip
              id="base-device-tooltip"
              content="Please refer to the ADP product manual for more details."
              links={[
                {
                  id: "link-1",
                  text: "ADP Manual",
                  href: externalUrls.mobility_devices_product_manual,
                },
              ]}
            />
          }
        />
      </CommentGroup>
      <CommentGroup
        section="section2c"
        id="2-reason"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <RadioGroup
          section="section2c"
          name="reason-for-application"
          label="Reason for Application"
          options={reasonOptions}
          requiredText="This is a required field."
          readOnly={mode !== "edit"}
        />
      </CommentGroup>
      <CommentGroup
        section="section2c"
        id="3-replacement-devices"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <CheckboxGroup
          section="section2c"
          options={replacementOptions}
          name="replacement-devices"
          label="Replacement Device(s) and/or Modifications Required Due To: (check as appropriate)"
          requiredText={
            isReplacementDevicesRequired
              ? "This is a required field."
              : undefined
          }
          readOnly={mode !== "edit"}
          tooltip={
            <Tooltip
              id="replacement-devices-tooltip"
              content="If you are selecting 'Previously funded ADP device is worn out', please provide a repair quote (Unless only seating needs to be replaced, then a quote is not required)."
            />
          }
        />
        <Modal
          title="Supporting Documents"
          isOpen={isUploadModalOpen}
          onClose={() => setUploadModalOpen(false)}
        >
          <UploadDocumentsModal
            clientFileId={clientFile.id}
            setUploadModalOpen={setUploadModalOpen}
          />
        </Modal>
      </CommentGroup>
      <CommentGroup
        section="section2c"
        id="4-confirmation-base"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <Text component="span" size="large" weight="bold">
          Confirmation of Applicant’s Eligibility for a Power Base: (answer
          required for each statement)
        </Text>
        <div className={styles.numberedRadioGroups}>
          <Text className={styles.numberBullet} weight="bold">
            1.
          </Text>
          <RadioGroup
            section="section2c"
            name="base-move-throughout-residence"
            description="Applicant requires the use of a power base to move independently throughout their place of residence."
            options={baseMoveThroughoutOptions}
            horizontal
            requiredText="This field is required."
            readOnly={mode !== "edit"}
          />
          <Text className={styles.numberBullet} weight="bold">
            2.
          </Text>
          <RadioGroup
            section="section2c"
            name="base-move-beyond-residence"
            description="Applicant requires the use of a power base to move independently beyond their place of residence."
            options={baseMoveBeyondOptions}
            horizontal
            requiredText="This field is required."
            readOnly={mode !== "edit"}
          />
        </div>
      </CommentGroup>
      <CommentGroup
        section="section2c"
        id="5-confirmation-scooter"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <Text component="span" size="large" weight="bold">
          Confirmation of Applicant’s Eligibility for A Power Scooter: (answer
          required for each statement)
        </Text>
        <div className={styles.numberedRadioGroups}>
          <Text className={styles.numberBullet} weight="bold">
            1.
          </Text>
          <RadioGroup
            section="section2c"
            name="scooter-move-throughout-residence"
            description="Applicant requires the use of a power scooter to move independently throughout their place of residence."
            options={scooterMoveThroughoutOptions}
            horizontal
            requiredText="This field is required."
            readOnly={mode !== "edit"}
          />
          <Text className={styles.numberBullet} weight="bold">
            2.
          </Text>
          <RadioGroup
            section="section2c"
            name="scooter-move-beyond-residence"
            description="Applicant requires the use of a power scooter to move independently beyond their place of residence."
            options={scooterMoveBeyondOptions}
            horizontal
            requiredText="This field is required."
            readOnly={mode !== "edit"}
          />
          <Text className={styles.numberBullet} weight="bold">
            3.
          </Text>
          <RadioGroup
            section="section2c"
            name="scooter-seat-tiller"
            description="Applicant operates the prescribed scooter independently with the standard scooter seat
          and tiller."
            options={scooterSeatTillerOptions}
            horizontal
            requiredText="This field is required."
            readOnly={mode !== "edit"}
          />
        </div>
      </CommentGroup>
      <CommentGroup
        section="section2c"
        id="6-prescription-details"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <Text size="large" weight="bold">
          Prescription Details for Power Device Only (answers required for all
          specifications){" "}
          <Tooltip
            id="prescription-details-tooltip"
            content="See product manual for details about all generic device types"
            links={[
              {
                id: "link-1",
                text: "Product Manual",
                href: externalUrls.mobility_devices_product_manual,
              },
            ]}
          />
        </Text>
        <Text size="large">Toggle All Units</Text>

        <ToggleAllUnitsButton
          section="section2c"
          setMetric={() => {
            toggleAllUnits("metric");
          }}
          setImperial={() => {
            toggleAllUnits("imperial");
          }}
          selectedUnit={selectedUnit}
          readOnly={mode !== "edit"}
        />
        <MeasurementInput
          required={prescriptionDetailsRequired}
          formSection="section2c"
          formFieldId="seat-width"
          label="Seat Width"
          type="length"
          valuePrefix="2c-seatWidth"
          readOnly={mode !== "edit"}
          tooltip={
            isSalesRep && (
              <Tooltip
                id="seat-width-tooltip"
                content="If seat width is greater than 18&Prime;, additional funding is available under funding code WEPA"
              />
            )
          }
        />
        <MeasurementInput
          required={prescriptionDetailsRequired}
          formSection="section2c"
          formFieldId="finished-back-height"
          label="Finished Back Height"
          type="length"
          valuePrefix="2c-finishedBackHeight"
          readOnly={mode !== "edit"}
        />
        <MeasurementInput
          required={prescriptionDetailsRequired}
          formSection="section2c"
          formFieldId="finished-seat-to-floor-height"
          formFieldUnitsId="finished-seat-to-floor-units"
          label="Finished Seat to Floor Height"
          type="length"
          valuePrefix="2c-finishedSeatToFloor"
          readOnly={mode !== "edit"}
        />
        <MeasurementInput
          formSection="section2c"
          formFieldId="leg-rest-length"
          label="Leg Rest Length"
          type="length"
          valuePrefix="2c-legRestLength"
          readOnly={mode !== "edit"}
        />
        <MeasurementInput
          required={prescriptionDetailsRequired}
          formSection="section2c"
          formFieldId="seat-depth"
          label="Seat Depth"
          type="length"
          valuePrefix="2c-seatDepth"
          readOnly={mode !== "edit"}
          tooltip={
            isSalesRep && (
              <Tooltip
                id="seat-depth-tooltip"
                content="If seat depth is greater than 18&Prime;, additional funding is available under funding code WEPB"
              />
            )
          }
        />
        <MeasurementInput
          required={
            prescriptionDetailsRequired || currDevice === "power-scooter"
          }
          formSection="section2c"
          formFieldId="client-weight"
          label="Client Weight"
          type="weight"
          valuePrefix="2c-clientWeight"
          readOnly={mode !== "edit"}
          tooltip={
            isSalesRep && (
              <Tooltip
                id="client-weight-tooltip"
                content="Additional funding is available under funding codes WEPF for 250-350 lbs, WEPG for 350-400 lbs, and WEPH for 400 lbs plus."
              />
            )
          }
        />
      </CommentGroup>
      <PageMarker page={8} total={13} />
      <CommentGroup
        section="section2c"
        id="7-additional-adp"
        className={styles.marginAfterPM}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <CheckboxGroup
          section="section2c"
          options={additionalAdpFundedOptions}
          name="additional-adp-funded-options"
          label="Additional ADP Funded Options Required for Prescribed Power Base: (check one or more)"
          readOnly={mode !== "edit"}
        />
      </CommentGroup>

      <CommentGroup
        section="section2c"
        id="8-specialty-options"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <CheckboxGroup
          section="section2c"
          options={specialityOptions}
          name="speciality-options"
          label="Specialty Components - Provide clinical rationale if any are selected *"
          readOnly={mode !== "edit"}
          tooltip={
            <Tooltip
              id="speciality-options-tooltip"
              content="For more information on the speciality components, please refer to the ADP manual. Please provide clinical rationale for the speciality component selected."
              links={[
                {
                  id: "link-1",
                  text: "ADP Manual",
                  href: externalUrls.mobility_devices_product_manual,
                },
              ]}
            />
          }
        />
        <br/>
        <TextArea
          section="section2c"
          id="medical-condition"
          label="* Provide Clinical Rationale"
          description="Please provide clinical rational for selected Specialty Components above."
          placeholder="Provide Clinical Rationale"
          maxLength={480}
          readOnly={mode !== "edit"}
          fieldOptions={nonSpecialCharacterOptions}
        />
      </CommentGroup>

      <CommentGroup
        section="section2c"
        id="9-power-positioning-devices"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <CheckboxGroup
          section="section2c"
          options={powerPositioningDevicesOptions}
          name="power-positioning-devices"
          label="Provide clinical rationale for the following Power Positioning Devices in Justification for Funding Chart"
          readOnly={mode !== "edit"}
          tooltip={
            <Tooltip
              id="power-positioning-devices-tooltip"
              content="Please refer to Section 525 of the mobility devices manual for the eligibility criteria. Please fill the funding chart. "
              links={[
                {
                  id: "link-1",
                  text: "Mobility Devices Manual",
                  href: externalUrls.mobility_devices_policy_and_administration_manual,
                },
                {
                  id: "link-2",
                  text: "Funding Chart (see 3rd link)",
                  href: externalUrls.ontario_gov_adp_form_downloads,
                },
              ]}
            />
          }
        />
      </CommentGroup>

      <CommentGroup
        section="section2c"
        id="10-non-adp-funded-options"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <TextGrid
          section="section2c"
          id="non-adp-funded-options-prescribed"
          label="Non ADP Funded Options Prescribed"
          maxInputs={9}
          maxLength={35}
          readOnly={mode !== "edit"}
          fieldOptions={nonSpecialCharacterOptions}
        />
      </CommentGroup>
      <CommentGroup
        section="section2c"
        id="11-setup-instructions"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <TextGrid
          section="section2c"
          id="setup-instructions-for-vendor"
          label="Set Up Instructions for Vendor"
          maxInputs={18}
          maxLength={35}
          readOnly={mode !== "edit"}
          fieldOptions={nonSpecialCharacterOptions}
        />
      </CommentGroup>

      <CommentGroup
        section="section2c"
        id="12-custom-modifications"
        className={styles.margin}
        commentBoxPosition={!checkCustomModifications ? "top" : undefined}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <RadioGroup
          section="section2c"
          name="custom-modifications-required"
          label="Custom Modifications Required"
          options={customModificationsOptions}
          horizontal
          requiredText="This field is required."
          readOnly={mode !== "edit"}
          tooltip={
            <Tooltip
              id="custom-modifications-tooltip"
              content="If custom modifications are made on a device, a custom modification quote is required that is specifically associated with the custom work. Please include the cost for parts and labour, as well as, clinical rationale that led to this custom modification being required. "
            />
          }
        />
        {checkCustomModifications && (
          <TextArea
            section="section2c"
            id="custom-modifications-clinical-rationale"
            label="Custom Modifications Clinical Rationale"
            description="The authorizer must provide clinical rationale to support the request in the space below and attach a vendor quote that provides
          a breakdown of the cost of labour (not to exceed $40.00/hour) and parts."
            placeholder="Custom modifications required - clinical rationale"
            maxLength={560}
            className={styles.paddingNoHorizontal}
            readOnly={mode !== "edit"}
            fieldOptions={nonSpecialCharacterOptions}
          />
        )}
      </CommentGroup>
      {fileStatus !== "Completed" && (
        <Modal
          isOpen={openPreviouslyFundedModal}
          title="Coverage for Mobility Devices"
          onClose={() => setOpenPreviouslyFundedModal(false)}
        >
          <div className={styles.modalText}>
            Please provide a repair quote (Unless only seating needs to be
            replaced, then a quote is not required).
          </div>
          <div className={styles.horizontalLine} />
          <div className={styles.modalButton}>
            <Button primary onClick={() => setOpenPreviouslyFundedModal(false)}>
              Close and return to form
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
};

export default Section2c;
